import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { JSONRecordData } from '../Log/graph.util';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from '../../../Resources/routes.json';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage } from 'react-intl';

interface PvViewProps {
  values: JSONRecordData;
  connected: boolean;
}

function PvView(props: PvViewProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }

  const currentLocation = useLocation();
  const navigate = useNavigate();

  // ✅ Flatten, sort, and assign unique displayId from 1-N
  const sortedPvView = props.values?.PvOnDc
    ? Object.entries(props.values.PvOnDc)
        .flatMap(([deviceId, device]) =>
          Object.entries(device.Strings).map(([pvId, pv], index) => ({
            pvId,
            pv,
            deviceId,
            displayId: `CU${deviceId}   ->   AMPT ${index + 1}`
          }))
        )
        .sort((a, b) => {
          if (a.deviceId === b.deviceId) {
            return parseInt(a.pvId) - parseInt(b.pvId);
          }
          return a.deviceId.localeCompare(b.deviceId);
        })
    : [];

  const [loading, setLoading] = useState(sortedPvView.length === 0);

  const handleMainStatsButton = () => {
    navigate(routes.mainstats);
  };

  useEffect(() => {
    if (sortedPvView.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [sortedPvView]);

  return (
    <>
      {!props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            <FormattedMessage id="unableToCommunicate" defaultMessage="Unable to communicate with the installation" />
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
          </Typography>
        </Container>
      )}

      {loading && props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            <FormattedMessage id="pvViewNotAvailable" defaultMessage="Pv view is not available at the moment" />
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
          </Typography>
        </Container>
      )}

      {!loading && props.connected && (
        <Container maxWidth="xl">
          {Object.entries(
            sortedPvView.reduce((acc, entry) => {
              if (!acc[entry.deviceId]) acc[entry.deviceId] = [];
              acc[entry.deviceId].push(entry);
              return acc;
            }, {} as Record<string, typeof sortedPvView>)
          ).map(([deviceId, entries]) => (
            <TableContainer
              key={deviceId}
              component={Paper}
              sx={{ marginTop: '30px', marginBottom: '40px', boxShadow: 3 }}
            >
              <Table sx={{ minWidth: 250 }} aria-label={`CU${deviceId} table`}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center"><FormattedMessage id="pv" defaultMessage="Pv" /></TableCell>
                    <TableCell align="center"><FormattedMessage id="power" defaultMessage="Power" /></TableCell>
                    <TableCell align="center"><FormattedMessage id="voltage" defaultMessage="Voltage" /></TableCell>
                    <TableCell align="center"><FormattedMessage id="current" defaultMessage="Current" /></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map(({ displayId, pv }, index) => (
                    <TableRow key={index}>
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 'bold', color: 'black' }}
                      >
                        {displayId}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor:
                            pv.Current === 0 ? '#FF033E' : '#32CD32'
                        }}
                      >
                        {pv.Power} W
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor:
                            pv.Current === 0 ? '#FF033E' : '#32CD32'
                        }}
                      >
                        {pv.Voltage} V
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontWeight: 'bold',
                          backgroundColor:
                            pv.Current === 0 ? '#FF033E' : '#32CD32'
                        }}
                      >
                        {pv.Current} A
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ))}
        </Container>
      )}
    </>
  );
}

export default PvView;
